import React, { useState, useEffect } from 'react';
import { ElectionService } from '../services/firebaseService';
import { Election } from '../types';
import CreateElectionModal from '../components/CreateElectionModal';
import EditElectionModal from '../components/EditElectionModal';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  PlayIcon,
  StopIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminElections: React.FC = () => {
  const [elections, setElections] = useState<Election[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedElection, setSelectedElection] = useState<Election | null>(null);

  useEffect(() => {
    const loadElections = async () => {
      try {
        const electionsData = await ElectionService.getAllElections();
        setElections(electionsData);
      } catch (error) {
        console.error('Erreur lors du chargement des élections:', error);
        setError('Erreur lors du chargement des élections');
      } finally {
        setLoading(false);
      }
    };

    loadElections();
  }, []);

  const handleToggleElection = async (electionId: string, isActive: boolean) => {
    try {
      await ElectionService.updateElection(electionId, { isActive: !isActive });
      setElections(prev => prev.map(e => 
        e.id === electionId ? { ...e, isActive: !isActive } : e
      ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'élection:', error);
    }
  };

  const handleCreateSuccess = () => {
    // Recharger les élections après création
    const loadElections = async () => {
      try {
        const electionsData = await ElectionService.getAllElections();
        setElections(electionsData);
      } catch (error) {
        console.error('Erreur lors du rechargement des élections:', error);
      }
    };
    loadElections();
  };

  const handleDeleteElection = async (electionId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette élection ? Cette action est irréversible.')) {
      try {
        await ElectionService.deleteElection(electionId);
        setElections(prev => prev.filter(e => e.id !== electionId));
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'élection:', error);
        setError('Erreur lors de la suppression de l\'élection');
      }
    }
  };

  const handleEditElection = (election: Election) => {
    setSelectedElection(election);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    // Recharger les élections après modification
    const loadElections = async () => {
      try {
        const electionsData = await ElectionService.getAllElections();
        setElections(electionsData);
      } catch (error) {
        console.error('Erreur lors du rechargement des élections:', error);
      }
    };
    loadElections();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Élections</h1>
            <p className="mt-2 text-gray-600">
              Créez et gérez les élections pour les postes exécutifs
            </p>
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Nouvelle élection
          </button>
        </div>
      </div>

      {/* Liste des élections */}
      <div className="space-y-6">
        {elections.length > 0 ? (
          elections.map((election) => (
            <div key={election.id} className="card">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {election.title}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      election.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {election.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{election.description}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    <span>Poste: {election.position}</span>
                    <span className="mx-2">•</span>
                    <span>{election.candidates.length} candidat(s)</span>
                    <span className="mx-2">•</span>
                    <span>
                      {election.startDate.toLocaleDateString()} - {election.endDate.toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleElection(election.id, election.isActive)}
                    className={`p-2 rounded-md ${
                      election.isActive 
                        ? 'text-red-600 hover:bg-red-50' 
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={election.isActive ? 'Désactiver' : 'Activer'}
                  >
                    {election.isActive ? (
                      <StopIcon className="w-5 h-5" />
                    ) : (
                      <PlayIcon className="w-5 h-5" />
                    )}
                  </button>
                  
                  <button 
                    onClick={() => handleEditElection(election)}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-md"
                    title="Modifier l'élection"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteElection(election.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    title="Supprimer l'élection"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Candidats */}
              {election.candidates.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Candidats</h4>
                  <div className="flex flex-wrap gap-2">
                    {election.candidates.map((candidate) => (
                      <span
                        key={candidate.id}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {candidate.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune élection</h3>
            <p className="mt-1 text-sm text-gray-500">
              Commencez par créer votre première élection.
            </p>
            <div className="mt-6">
              <button 
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Créer une élection
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de création d'élection */}
      <CreateElectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Modal d'édition d'élection */}
      <EditElectionModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedElection(null);
        }}
        onSuccess={handleEditSuccess}
        election={selectedElection}
      />
    </div>
  );
};

export default AdminElections;
